import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { message, Spin, Modal, Dropdown, Input } from 'antd';
import {
  UserOutlined,
  RobotOutlined,
  NotificationOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  LogoutOutlined,
  LockOutlined,
  RightOutlined,
  BulbOutlined,
  CodeOutlined,
  DatabaseOutlined,
  GlobalOutlined,
  CloudOutlined,
  BookOutlined,
  CopyOutlined,
  KeyOutlined,
  ApiOutlined
} from '@ant-design/icons';
import './UserDashboard.css';
/**
 * 从用户端 Presenter 导入真实接口
 * getAgents: 查询启用中的AI工具列表（接口文档 #7）
 * getAnnouncements: 查询公告列表（接口文档 #11）
 * getTokenUsage: 查询当前用户Token用量（接口文档 #15）
 */
import * as UserPresenter from './UserDashboardPresenter';
import { getLocalStorage, setItem } from '../../utils/localStorageUtil';
import { logoutPortalSession } from '../../utils/portalSession';
import { LOCAL_STORAGE_KEYS } from '../../const/localStorageConst';
import { resolveAgentIcon } from '../../const/agentIcons';
import { usePasswordUpdateModal } from '../../components/PasswordUpdateModalProvider';
import fgLogo from '../../imgs/fg_logo.png';

const getDisplayUsername = (username) => {
  if (!username) return '用户';
  return String(username).split('@')[0] || '用户';
};

const normalizeReadAnnouncements = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [value];
    }
  }

  return [value];
};

const UserDashboard = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const { openPasswordUpdateModal } = usePasswordUpdateModal();

  // 状态
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState({});
  const [tokenUsage, setTokenUsage] = useState({ today: {}, week: {}, month: {} });
  const [agents, setAgents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [readAnnouncements, setReadAnnouncements] = useState(() => {
    const stored = getLocalStorage(LOCAL_STORAGE_KEYS.READ_ANNOUNCEMENTS);
    return normalizeReadAnnouncements(stored);
  });
  const [announcementDetail, setAnnouncementDetail] = useState(null);
  const displayUsername = getDisplayUsername(userInfo.username);

  // 平台使用手册下拉菜单
  const manualMenuItems = [
    { key: 'chat', label: '大模型对话平台使用手册', url: 'https://chat.henjiu.com/大模型对话平台使用手册.html' },
    { key: 'token', label: '令牌管理平台使用手册', url: 'https://chat.henjiu.com/令牌管理平台使用手册.html' },
    { key: 'fxz', label: '富小助平台使用手册', url: 'https://chat.henjiu.com/富小助平台使用手册.html' },
  ];

  // 加载数据 — 调用真实接口（Presenter 层封装）
  const loadData = async () => {
    setLoading(true);
    try {
      const [agentsRes, announcementsRes, tokenRes] = await Promise.all([
        UserPresenter.getAgents({ status: '1' }),
        UserPresenter.getAnnouncements(),
        UserPresenter.getTokenUsage()
      ]);

      if (agentsRes.status === 200) {
        setAgents(agentsRes.agents);
      }
      if (announcementsRes.status === 200) {
        setAnnouncements(announcementsRes.public_statements);
      }
      if (tokenRes.status === 200) {
        setTokenUsage(tokenRes.data);
      }
    } catch (error) {
      messageApi.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedUserInfo = getLocalStorage(LOCAL_STORAGE_KEYS.USER_INFO);
    if (storedUserInfo) {
      setUserInfo(storedUserInfo);
    }
    loadData();
  }, []);

  const handleLogout = async () => {
    await logoutPortalSession();
    navigate('/login');
  };

  // 获取工具图标
  // 优先使用管理员配置的自定义图标（agent_icon）；
  // 未配置时回退：「令牌管理」「MCP 管理平台」都是 agent_type=data，按名称区分避免重复
  const getToolIcon = (tool = {}) => {
    const customIcon = resolveAgentIcon(tool.agent_icon);
    if (customIcon) {
      const Custom = customIcon;
      return <Custom />;
    }
    const name = String(tool.agent_name || '');
    if (name.includes('MCP')) return <ApiOutlined />;
    if (name.includes('令牌')) return <KeyOutlined />;
    const iconMap = {
      chat: <BulbOutlined />,
      code: <CodeOutlined />,
      data: <DatabaseOutlined />,
      translate: <GlobalOutlined />,
      pan: <CloudOutlined />
    };
    return iconMap[tool.agent_type] || <RobotOutlined />;
  };

  // 格式化数字：小于万用具体数字，万以上百万以下用万单位，亿以上用亿单位
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    if (num < 10000) {
      return num.toLocaleString();
    } else if (num < 100000000) {
      return (num / 10000).toFixed(1).replace(/\.0$/, '') + '万';
    } else {
      return (num / 100000000).toFixed(1).replace(/\.0$/, '') + '亿';
    }
  };

  // 格式化时间（带年份，公告列表用）
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const date = new Date(timeStr);
    return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // 为AI工具跳转链接追加登录账号和密码参数。
  // 当工具不支持邮箱登录时，name 只传邮箱 @ 前的账号部分。
  // 使用 URL/URLSearchParams 保证已有 query 参数时也能正确追加。
  // 特殊说明：个人网盘（agent_type='pan'）和大模型对话（llm.henjiu.com）不拼账密，走 OIDC SSO。
  //
  // 目标系统差异处理（2026-10-17 修复纯数字密码跳转报错）：
  // new-api（api.henjiu.com）用 JSON.parse 解析 URL 参数，纯数字值会被解析成 number，
  // 导致其 z.string() 校验失败显示 500；需按 JSON 字符串字面量发送（加引号）。
  const toJsonSafeSearchParam = (value) => {
    const raw = String(value);
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'string') {
        return JSON.stringify(raw);
      }
    } catch {
      // 非合法 JSON 字面量，目标系统会回退为原始字符串，保持原样
    }
    return raw;
  };

  const getToolJumpHost = (tool) => {
    try {
      return new URL(tool.agent_jump_url, window.location.origin).hostname;
    } catch {
      return '';
    }
  };

  const isOpenWebUITool = (tool) => getToolJumpHost(tool) === 'llm.henjiu.com';
  const isNewApiTool = (tool) => getToolJumpHost(tool) === 'api.henjiu.com';

  const buildToolJumpUrl = (rawUrl, tool = {}) => {
    if (!rawUrl) return '';

    // 网盘类工具不携带账密跳转
    if (tool.agent_type === 'pan') {
      return rawUrl;
    }

    const username = String(getLocalStorage(LOCAL_STORAGE_KEYS.LOGIN_USERNAME) || userInfo.username || '');
    const jumpName = Number(tool.is_support_email) === 0 ? username.split('@')[0] || username : username;
    const pwd = String(getLocalStorage(LOCAL_STORAGE_KEYS.LOGIN_PWD) || '');

    try {
      const url = new URL(rawUrl, window.location.origin);
      let nameValue = jumpName;
      let pwdValue = pwd;
      if (url.hostname === 'api.henjiu.com') {
        // new-api：避免纯数字等值被其 JSON.parse 解析为非字符串
        nameValue = toJsonSafeSearchParam(nameValue);
        pwdValue = toJsonSafeSearchParam(pwdValue);
      }
      url.searchParams.set('name', nameValue);
      url.searchParams.set('pwd', pwdValue);
      return url.toString();
    } catch {
      const separator = rawUrl.includes('?') ? '&' : '?';
      return `${rawUrl}${separator}name=${encodeURIComponent(jumpName)}&pwd=${encodeURIComponent(pwd)}`;
    }
  };

  // 网盘、大模型对话和令牌管理先建立 OIDC cookie，再打开目标系统；预开窗口避免异步请求后被拦截。
  const handleToolClick = async (tool) => {
    if (!tool.agent_jump_url) return;
    const isPan = tool.agent_type === 'pan';
    const isOpenWebUI = isOpenWebUITool(tool);
    const isNewApi = isNewApiTool(tool);

    if (!isPan && !isOpenWebUI && !isNewApi) {
      window.open(buildToolJumpUrl(tool.agent_jump_url, tool), '_blank');
      return;
    }

    let destination;
    let loginLabel = '网盘';
    try {
      if (isPan) {
        destination = new URL(tool.agent_jump_url, window.location.origin);
        if (destination.origin !== 'https://pan.ai.henjiu.com') throw new Error('网盘域名不匹配');
        // 网盘不需要 URL 凭证；完全丢弃 query，避免旧链接携带任意形式的 token/密码。
        destination.search = '';
        destination.pathname = '/api/auth/sso';
        destination.searchParams.set('method', 'sso_get_token');
      } else if (isOpenWebUI) {
        // Open WebUI：不带任何账密参数，直接进入它自己的 OIDC 登录入口
        loginLabel = '大模型对话';
        destination = new URL('https://llm.henjiu.com/oauth/oidc/login');
      } else {
        // new-api：同源获取 OAuth state 后进入富小助授权页；登录页仅作为失败兜底
        loginLabel = '令牌管理';
        destination = new URL('https://api.henjiu.com/sign-in');
      }
    } catch (error) {
      messageApi.error(error.message || `${loginLabel}链接无效`);
      return;
    }

    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const token = getLocalStorage(LOCAL_STORAGE_KEYS.TOKEN);
      if (!token) throw new Error('请重新登录后再试');
      const response = await fetch('/fg-ai-center/oidc/session', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { token: String(token) },
      });
      if (!response.ok) throw new Error('单点登录失败，请重试');

      let targetUrl = destination.toString();
      if (isNewApi) {
        // new-api 的 state 绑定在它自己的 session cookie 上，必须由浏览器在 api.henjiu.com 同源获取。
        // Nginx 已在 /api/oauth/state 上放行 chat.henjiu.com 的 CORS 凭据请求。
        const stateResponse = await fetch('https://api.henjiu.com/api/oauth/state?aff=', {
          credentials: 'include',
        });
        if (!stateResponse.ok) throw new Error('单点登录启动失败，请重试');
        const stateJson = await stateResponse.json();
        if (!stateJson.success || !stateJson.data) throw new Error('单点登录启动失败，请重试');
        const authUrl = new URL('https://chat.henjiu.com/oidc/authorize');
        authUrl.searchParams.set('client_id', 'newapi');
        authUrl.searchParams.set('redirect_uri', 'https://api.henjiu.com/oauth/newapi');
        authUrl.searchParams.set('response_type', 'code');
        authUrl.searchParams.set('scope', 'openid profile email');
        authUrl.searchParams.set('state', stateJson.data);
        targetUrl = authUrl.toString();
      }

      if (tab && !tab.closed) {
        tab.location.replace(targetUrl);
      } else {
        window.location.assign(targetUrl);
      }
    } catch (error) {
      if (isNewApi && tab && !tab.closed) {
        // new-api 启动失败时仍落到登录页，SSO 按钮仍可手动点击。
        tab.location.replace('https://api.henjiu.com/sign-in');
        return;
      }
      if (tab && !tab.closed) tab.close();
      messageApi.error(error.message || `${loginLabel}登录失败`);
    }
  };

  // 复制 AI 对接提示词弹窗
  const [panPromptVisible, setPanPromptVisible] = useState(false);
  const [panPromptText, setPanPromptText] = useState('');

  const handleShowPanPrompt = (e) => {
    e.stopPropagation(); // 阻止触发卡片跳转
    const username = String(getLocalStorage(LOCAL_STORAGE_KEYS.LOGIN_USERNAME) || userInfo.username || '');
    const pwd = String(getLocalStorage(LOCAL_STORAGE_KEYS.LOGIN_PWD) || '');
    if (!username || !pwd) {
      messageApi.error('登录信息不完整，请重新登录');
      return;
    }

    const prompt = `# 富小助网盘 WebDAV 对接

## 接入信息
- WebDAV URL: https://pan.ai.henjiu.com/dav/
- 用户名: ${username}
- 密码: ${pwd}
- 存储路径: 你的文件存储在 /移动/曙光数据/${username}/ 目录下

## 使用说明
1. 上传文件时，路径使用相对路径（如 /dav/test.txt），实际会落到你的个人目录
2. 不要使用绝对路径（如 /dav/移动/曙光数据/${username}/test.txt），否则会嵌套目录
3. 支持操作：上传（PUT）、下载（GET）、列目录（PROPFIND）、创建目录（MKCOL）、删除（DELETE）、移动（MOVE）

## Python 示例
\`\`\`python
import requests

# 上传文件
resp = requests.put(
    'https://pan.ai.henjiu.com/dav/hello.txt',
    auth=('${username}', '${pwd}'),
    data=b'Hello World',
    headers={'Content-Type': 'application/octet-stream'},
)
print(resp.status_code)  # 201 表示创建成功

# 下载文件
resp = requests.get(
    'https://pan.ai.henjiu.com/dav/hello.txt',
    auth=('${username}', '${pwd}'),
)
print(resp.text)
\`\`\`

## curl 示例
\`\`\`bash
# 上传
curl -X PUT "https://pan.ai.henjiu.com/dav/test.txt" \\
  --user "${username}:${pwd}" \\
  --data-binary @local-file.txt

# 下载
curl -X GET "https://pan.ai.henjiu.com/dav/test.txt" \\
  --user "${username}:${pwd}" \\
  -o downloaded.txt -L
\`\`\`
`;
    setPanPromptText(prompt);
    setPanPromptVisible(true);
  };

  const handleCopyPanPrompt = async () => {
    try {
      await navigator.clipboard.writeText(panPromptText);
      messageApi.success('已复制到剪贴板');
    } catch (err) {
      messageApi.error('复制失败，请手动选择文本复制');
      console.error('复制失败:', err);
    }
  };

  // 点击公告查看详情
  const handleAnnouncementClick = (item) => {
    const normalizedReadAnnouncements = normalizeReadAnnouncements(readAnnouncements);

    // 标记为已读
    if (!normalizedReadAnnouncements.includes(item.id)) {
      const newRead = [...normalizedReadAnnouncements, item.id];
      setReadAnnouncements(newRead);
      setItem(LOCAL_STORAGE_KEYS.READ_ANNOUNCEMENTS, newRead);
    }
    // 显示详情
    setAnnouncementDetail(item);
  };

  if (loading) {
    return (
      <div className="user-dashboard" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="user-dashboard">
      {contextHolder}

      {/* 背景装饰 */}
      <div className="user-dashboard__glow user-dashboard__glow--1" />
      <div className="user-dashboard__glow user-dashboard__glow--2" />
      <div className="user-dashboard__glow user-dashboard__glow--3" />

      {/* 顶部导航 */}
      <header className="user-header">
        <div className="user-header__left">
        <div className="user-header__logo">
          <img src={fgLogo} alt="logo" className="user-header__logo-img" />
          <div>
            <div className="user-header__brand">富小助平台</div>
            <div className="user-header__brand-sub">AI 智能助手</div>
          </div>
        </div>
        {/* 平台使用手册下拉菜单 */}
        <Dropdown
          menu={{
            items: manualMenuItems.map((item) => ({
              key: item.key,
              label: (
                <a href={item.url} target="_blank" rel="noreferrer" className="user-header__manual-link">
                  {item.label}
                </a>
              ),
            })),
          }}
          trigger={['hover']}
          overlayClassName="user-header__manual-dropdown"
        >
          <div className="user-header__manual">
            <BookOutlined style={{ marginRight: 6 }} />
            <span>平台使用手册</span>
          </div>
        </Dropdown>
        </div>
        <div className="user-header__right">
          <div className="user-header__user">
            <div className="user-header__user-avatar">
              {displayUsername.charAt(0).toUpperCase() || 'U'}
            </div>
            <span className="user-header__user-name">
              {displayUsername}
            </span>
          </div>
          <button
            className="user-header__password"
            onClick={() => openPasswordUpdateModal({ forced: false, source: 'manual' })}
          >
            <LockOutlined style={{ marginRight: 6 }} />
            修改密码
          </button>
          <button className="user-header__logout" onClick={handleLogout}>
            <LogoutOutlined style={{ marginRight: 6 }} />
            退出
          </button>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="user-content">
        {/* 欢迎区域 */}
        <div className="user-welcome">
          <h1 className="user-welcome__title">
            你好，{displayUsername} 👋
          </h1>
          <p className="user-welcome__subtitle">
            欢迎使用富小助 AI 助手平台，这里是你的智能工具中心
          </p>
        </div>

        {/* Token用量卡片 */}
        <div className="token-cards">
          <div className="token-card token-card--blue">
            <div className="token-card__label">
              <ClockCircleOutlined />
              今日用量
            </div>
            <div className="token-card__value">
              {formatNumber(tokenUsage.today?.total_tokens)}
              <span className="token-card__unit">tokens</span>
            </div>
            <div className="token-card__sub">
              输入: {formatNumber(tokenUsage.today?.prompt_tokens)} | 输出: {formatNumber(tokenUsage.today?.completion_tokens)}
            </div>
            <div className="token-card__icon">
              <ThunderboltOutlined />
            </div>
          </div>

          <div className="token-card token-card--green">
            <div className="token-card__label">
              <CalendarOutlined />
              本周用量
            </div>
            <div className="token-card__value">
              {formatNumber(tokenUsage.week?.total_tokens)}
              <span className="token-card__unit">tokens</span>
            </div>
            <div className="token-card__sub">
              输入: {formatNumber(tokenUsage.week?.prompt_tokens)} | 输出: {formatNumber(tokenUsage.week?.completion_tokens)}
            </div>
            <div className="token-card__icon">
              <ThunderboltOutlined />
            </div>
          </div>

          <div className="token-card token-card--purple">
            <div className="token-card__label">
              <CalendarOutlined />
              本月用量
            </div>
            <div className="token-card__value">
              {formatNumber(tokenUsage.month?.total_tokens)}
              <span className="token-card__unit">tokens</span>
            </div>
            <div className="token-card__sub">
              输入: {formatNumber(tokenUsage.month?.prompt_tokens)} | 输出: {formatNumber(tokenUsage.month?.completion_tokens)}
            </div>
            <div className="token-card__icon">
              <ThunderboltOutlined />
            </div>
          </div>
        </div>

        {/* AI工具与公告水平布局 */}
        <div className="ai-tools-layout">
          {/* 左侧：AI工具区域 */}
          <section className="ai-tools module-card">
            <div className="module-card__header">
              <div className="section-title">
                <div className="section-title__icon section-title__icon--purple">
                  <RobotOutlined />
                </div>
                <span className="section-title__text">AI 工具中心</span>
                <div className="section-title__line" />
              </div>
            </div>
            <div className="module-card__content ai-tools-grid-wrapper">
              <div className="ai-tools-grid">
                {agents.length > 0 ? (
                  agents.map((tool) => (
                    <div
                      key={tool.agent_id}
                      className="ai-tool-card"
                      onClick={() => handleToolClick(tool)}
                    >
                      <div className="ai-tool-card__header">
                        <div className={`ai-tool-card__icon ai-tool-card__icon--${tool.agent_type}`}>
                          {getToolIcon(tool)}
                        </div>
                        <div className="ai-tool-card__info">
                          <h3 className="ai-tool-card__name">{tool.agent_name}</h3>
                          <div className="ai-tool-card__tags">
                            {tool.agent_tags?.split(',').map((tag, i) => (
                              <span key={i} className="ai-tool-card__tag">{tag.trim()}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="ai-tool-card__desc">{tool.agent_des}</p>
                      <div className="ai-tool-card__footer">
                        <div className={`ai-tool-card__status ${tool.status === '1' ? 'ai-tool-card__status--active' : 'ai-tool-card__status--maintenance'}`}>
                          {tool.status === '1' ? '可用' : '维护中'}
                        </div>
                        {tool.agent_type === 'pan' && tool.status === '1' && (
                          <button
                            className="ai-tool-card__btn ai-tool-card__btn--copy"
                            onClick={handleShowPanPrompt}
                            title="查看 AI 对接提示词"
                          >
                            <CopyOutlined />
                          </button>
                        )}
                        <button
                          className="ai-tool-card__btn"
                          disabled={tool.status !== '1'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToolClick(tool);
                          }}
                        >
                          立即体验 <RightOutlined />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="user-empty" style={{ gridColumn: '1 / -1' }}>
                    <div className="user-empty__icon">
                      <RobotOutlined />
                    </div>
                    <div className="user-empty__text">暂无可用工具</div>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* 右侧：平台公告 */}
          <section className="announcements announcements-sidebar module-card">
            <div className="module-card__header">
              <div className="section-title">
                <div className="section-title__icon section-title__icon--blue">
                  <NotificationOutlined />
                </div>
                <span className="section-title__text">平台公告</span>
                <div className="section-title__line" />
              </div>
            </div>
            <div className="module-card__content">
              <div className="announcement-list">
                {announcements.length > 0 ? (
                  announcements.map((item) => (
                    <div
                      key={item.id}
                      className="announcement-item"
                      onClick={() => handleAnnouncementClick(item)}
                      style={{ cursor: 'pointer' }}
                    >
                      {(() => {
                        const normalizedReadAnnouncements = normalizeReadAnnouncements(readAnnouncements);
                        return !normalizedReadAnnouncements.includes(item.id) && <div className="announcement-item__dot" />;
                      })()}
                      <div className="announcement-item__icon">
                        <NotificationOutlined />
                      </div>
                      <div className="announcement-item__content">
                        <div className="announcement-item__text">{item.public_content}</div>
                        <div className="announcement-item__time">{formatTime(item.created_time)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="user-empty">
                    <div className="user-empty__icon">
                      <NotificationOutlined />
                    </div>
                    <div className="user-empty__text">暂无公告</div>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* 公告详情弹窗 */}
      <Modal
        title="公告详情"
        open={!!announcementDetail}
        onCancel={() => setAnnouncementDetail(null)}
        footer={null}
        centered
      >
        {announcementDetail && (
          <div>
            <p style={{ marginBottom: 16, color: '#666' }}>
              发布时间：{formatTime(announcementDetail.created_time)}
            </p>
            <p style={{ fontSize: 15, lineHeight: 1.8 }}>
              {announcementDetail.public_content}
            </p>
          </div>
        )}
      </Modal>

      {/* AI 对接提示词弹窗 */}
      <Modal
        title="AI 对接提示词"
        open={panPromptVisible}
        onCancel={() => setPanPromptVisible(false)}
        footer={[
          <button key="copy" className="ai-tool-card__btn" onClick={handleCopyPanPrompt}>
            <CopyOutlined /> 复制到剪贴板
          </button>,
          <button key="close" className="ai-tool-card__btn ai-tool-card__btn--copy" onClick={() => setPanPromptVisible(false)}>
            关闭
          </button>,
        ]}
        width={800}
        centered
      >
        <Input.TextArea
          value={panPromptText}
          rows={20}
          readOnly
          style={{ fontFamily: 'monospace', fontSize: 13 }}
        />
        <p style={{ marginTop: 12, color: '#666', fontSize: 13 }}>
          点击「复制到剪贴板」按钮，或手动选择文本复制后粘贴给 AI。
        </p>
      </Modal>
    </div>
  );
};

export default UserDashboard;